import { z } from 'zod';
import { LeadCommunicationReferenceSchema } from './communications.js';

/**
 * @aiPurpose closed catalog of Lead activity event kinds (#1076).
 *   Mirrors the Prisma `LeadActivityKind` enum.
 * @aiInvariant append-only; activity rows reference these literals
 *   forever. Adding a kind requires a paired Prisma migration.
 */
export enum LeadActivityKind {
  LEAD_CREATED = 'LEAD_CREATED',
  LEAD_STAGE_CHANGED = 'LEAD_STAGE_CHANGED',
  LEAD_ASSIGNED = 'LEAD_ASSIGNED',
  LEAD_UNASSIGNED = 'LEAD_UNASSIGNED',
  LEAD_CLIENT_BOUND = 'LEAD_CLIENT_BOUND',
  LEAD_CLIENT_UNBOUND = 'LEAD_CLIENT_UNBOUND',
  LEAD_TASK_CREATED = 'LEAD_TASK_CREATED',
  LEAD_TASK_COMPLETED = 'LEAD_TASK_COMPLETED',
  LEAD_COMMENT_ADDED = 'LEAD_COMMENT_ADDED',
  LEAD_COMMENT_EDITED = 'LEAD_COMMENT_EDITED',
  LEAD_ARCHIVED = 'LEAD_ARCHIVED',
  LEAD_RESTORED = 'LEAD_RESTORED',
  LEAD_REQUEST_LINKED = 'LEAD_REQUEST_LINKED',
  LEAD_REQUEST_UNLINKED = 'LEAD_REQUEST_UNLINKED',
  LEAD_FILE_UPLOADED = 'LEAD_FILE_UPLOADED',
  LEAD_FILE_DELETED = 'LEAD_FILE_DELETED',
  // WHY (#1256): emitted when an external call-center submission creates
  //   a Lead; the actor is the submitting agent. Kept in lockstep with
  //   the Prisma `LeadActivityKind` enum in
  //   `apps/backend/prisma/schema/leads.prisma` (append-only).
  LEAD_INTAKE_RECEIVED = 'LEAD_INTAKE_RECEIVED',
  // WHY (#1807): typed call/message capture for the AI-foundation epic
  // (#1798 §7). Append-only — mirrored in Prisma LeadActivityKind.
  CALL = 'CALL',
  MESSAGE = 'MESSAGE',
  // WHY (#2319): task soft-cancel / reopen activity kinds. Append-only,
  // mirrored in Prisma LeadActivityKind. Added by the compatibility runway
  // before #2317 emits any row of these kinds.
  LEAD_TASK_CANCELLED = 'LEAD_TASK_CANCELLED',
  LEAD_TASK_REOPENED = 'LEAD_TASK_REOPENED',
}
export const LeadActivityKindSchema = z.nativeEnum(LeadActivityKind);

/**
 * Opt-in request header that makes the Lead activity feed INCLUDE the
 * `LEAD_TASK_CANCELLED` / `LEAD_TASK_REOPENED` kinds. Absent/other values keep
 * the default response, which EXCLUDES them so a deployed strict client that
 * predates those kinds never receives one (rolling-deploy / rollback safety,
 * #2319). Header name is case-insensitive per HTTP; the value is matched
 * case-insensitively by the backend.
 */
export const LEAD_ACTIVITY_V2_HEADER = 'X-Magimp-Lead-Activity-V2';
export const LEAD_ACTIVITY_V2_TASK_CANCEL_REOPEN = 'task-cancel-reopen';

/**
 * @aiPurpose wire projection of a `LeadActivity` row.
 * @aiPii `metadata` carries entity ids and typed deltas only; never
 *   PII snapshots (#1075). Display names resolve at read time subject
 *   to actor visibility.
 */
export const LeadActivitySchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    actorId: z.string().uuid().nullable(),
    kind: LeadActivityKindSchema,
    metadata: z.record(z.unknown()),
    occurredAt: z.string().datetime(),
  })
  .strict();
export type LeadActivity = z.infer<typeof LeadActivitySchema>;

/**
 * @aiPurpose actor reference for a feed row resolved at read time.
 *   NULL means the activity was system-emitted (e.g. webhook intake)
 *   or the user row was hard-deleted with `SetNull` cascading the FK.
 */
export const LeadActivityActorRefSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string().min(1),
  })
  .strict();
export type LeadActivityActorRef = z.infer<typeof LeadActivityActorRefSchema>;

/**
 * @aiPurpose resolved display references for a feed row. The backend
 *   feed assembler resolves entity ids in `LeadActivity.metadata`
 *   against the live tables and emits each resolution under the
 *   matching slot. A NULL slot signals "referenced entity no longer
 *   exists or is hard-pruned" — the wire format keeps the slot so the
 *   client can render an explicit `(deleted file)` / `(deleted
 *   comment)` placeholder instead of inventing one or 404-ing the row.
 * @aiPii `file.fileName`, `comment.bodyPreview`, and
 *   `communication.text`/`communication.transcript` are PII; the
 *   backend only assembles them inside a Lead-visibility gate. The
 *   wire row never carries the full comment body — see
 *   `LeadCommentSchema` for that path.
 * @aiInvariant each slot is `.optional()` for rolling-deploy tolerance
 *   — an older backend that does not yet emit a slot does not fail the
 *   client parse. A present slot is either the resolved object or
 *   `null` (entity deleted / not found).
 */
export const LeadActivityReferencesSchema = z
  .object({
    file: z
      .object({ id: z.string().uuid(), fileName: z.string().min(1) })
      .strict()
      .nullable()
      .optional(),
    comment: z
      .object({ id: z.string().uuid(), bodyPreview: z.string() })
      .strict()
      .nullable()
      .optional(),
    // WHY (#1807): CALL/MESSAGE activity rows resolve their paired
    // LeadCommunication here. `.optional()` preserves rolling-deploy
    // compatibility — an older deployment that does not emit this slot
    // does not break existing clients.
    communication: LeadCommunicationReferenceSchema.nullable().optional(),
  })
  .strict();
export type LeadActivityReferences = z.infer<typeof LeadActivityReferencesSchema>;

/**
 * @aiPurpose paginated activity-feed row. Combines the raw
 *   `LeadActivity` projection with read-time-resolved actor and
 *   reference slots.
 * @aiInvariant `metadata` keeps the canonical typed-id payload; the
 *   `actor`/`references` slots are read-side conveniences and MUST
 *   NOT be the only source of truth for any audit decision.
 */
export const LeadActivityFeedItemSchema = z
  .object({
    id: z.string().uuid(),
    leadId: z.string().uuid(),
    kind: LeadActivityKindSchema,
    occurredAt: z.string().datetime(),
    actor: LeadActivityActorRefSchema.nullable(),
    metadata: z.record(z.unknown()),
    references: LeadActivityReferencesSchema,
  })
  .strict();
export type LeadActivityFeedItem = z.infer<typeof LeadActivityFeedItemSchema>;

export const LEAD_ACTIVITY_FEED_LIMIT_DEFAULT = 50;
export const LEAD_ACTIVITY_FEED_LIMIT_MAX = 100;

/**
 * @aiPurpose query envelope for the paginated activity-feed endpoint.
 * @aiInvariant `cursor` is opaque to the client; the backend mints
 *   and parses it. `limit` bounds page size so a client cannot pull
 *   the entire feed in a single roundtrip; the soft default is 50
 *   and the hard cap is 100.
 */
export const LeadActivityFeedQuerySchema = z
  .object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(LEAD_ACTIVITY_FEED_LIMIT_MAX)
      .default(LEAD_ACTIVITY_FEED_LIMIT_DEFAULT),
  })
  .strict();
export type LeadActivityFeedQuery = z.infer<typeof LeadActivityFeedQuerySchema>;

export const LeadActivityFeedResponseSchema = z
  .object({
    items: z.array(LeadActivityFeedItemSchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type LeadActivityFeedResponse = z.infer<typeof LeadActivityFeedResponseSchema>;
